/**
 * NoShowActionButton — Growth Engine adjacent, Operational Truth (Feb 17, 2026).
 *
 * Instructor-only consumer surface for the canonical NO_SHOW
 * helper. Two-step confirmation per
 * /app/memory/PLATFORM_AUTHORITY_DOCTRINE.md §9 (human confirmation
 * required for impactful reversible actions).
 *
 * Doctrine references:
 *   /app/memory/NO_SHOW_DOCTRINE_SPEC.md
 *   /app/memory/PLATFORM_AUTHORITY_DOCTRINE.md
 *   /app/memory/OPERATIONAL_TRUTH_ATLAS.md
 *
 * What this surface does:
 *   - Renders a calm "Pupil didn't arrive" action.
 *   - Two-step: tap → modal → confirm.
 *   - On 400 with "15-minute grace" wording, surfaces the calm
 *     refusal verbatim (the backend owns the wording).
 *   - On success, calls onMarked() so the parent can refresh.
 *
 * What this surface does NOT do:
 *   - Does NOT auto-trigger on any timer or sensor signal.
 *   - Does NOT live inside the start-lesson button (Doctrine §4d).
 *   - Does NOT notify the learner.
 *   - Does NOT compute refunds.
 *   - Does NOT call any AI service.
 */
import { useState } from "react";
import axios from "axios";
import { UserX, X, Check } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const REASONS = [
  { id: "no_arrival",                       label: "Pupil did not arrive" },
  { id: "no_response",                      label: "Pupil did not respond" },
  { id: "instructor_present_pupil_absent",  label: "I was on-site, pupil wasn't" },
  { id: "other",                            label: "Other (notes optional)" },
];

export default function NoShowActionButton({ lessonEventId, onMarked }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("no_arrival");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (submitting) return;
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/api/no-show/mark`,
        { lesson_event_id: lessonEventId, reason, notes: notes || null },
        { withCredentials: true },
      );
      if (r.data?.already_no_show) {
        toast("Already recorded as a no-show.");
      } else {
        toast.success("Recorded — pupil did not arrive.");
      }
      setOpen(false);
      onMarked?.(r.data);
    } catch (err) {
      // Backend owns the calm refusal wording (e.g.
      // "It's too early to record a no-show — please wait until
      // the standard 15-minute grace window has passed…").
      const detail = err?.response?.data?.detail || "Could not record no-show.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <button
        type="button"
        data-testid="no-show-open-btn"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-[12px] font-bold text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700"
      >
        <UserX className="w-3.5 h-3.5" />
        Pupil didn't arrive
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[1200] flex items-end md:items-center justify-center"
          data-testid="no-show-modal"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
            onClick={() => !submitting && setOpen(false)}
          />
          <div className="relative bg-white dark:bg-slate-900 w-full md:max-w-md md:rounded-2xl rounded-t-2xl border border-slate-200 dark:border-slate-800 shadow-xl p-5 mx-0 md:mx-4">
            <header className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="text-base font-bold text-slate-900 dark:text-white">
                  Record a no-show
                </p>
                <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-0.5">
                  Use this if the pupil did not arrive after the standard 15-minute grace period.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="no-show-close-btn"
                className="w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center"
                aria-label="Close"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </header>

            <fieldset className="space-y-2 mb-4">
              <legend className="text-[11px] uppercase tracking-wide font-bold text-slate-500 mb-1.5">
                What happened?
              </legend>
              {REASONS.map((r) => (
                <label
                  key={r.id}
                  data-testid={`no-show-reason-${r.id}`}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border cursor-pointer transition-colors ${
                    reason === r.id
                      ? "border-slate-700 bg-slate-50 dark:bg-slate-800"
                      : "border-slate-200 dark:border-slate-700"
                  }`}
                >
                  <input
                    type="radio"
                    name="no-show-reason"
                    value={r.id}
                    checked={reason === r.id}
                    onChange={() => setReason(r.id)}
                    className="accent-slate-700"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-200">{r.label}</span>
                </label>
              ))}
            </fieldset>

            <label className="block mb-4">
              <span className="text-[11px] uppercase tracking-wide font-bold text-slate-500">
                Notes (optional)
              </span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="no-show-notes"
                rows={2}
                maxLength={2000}
                placeholder="Three texts, no response. Waited 20 minutes."
                className="mt-1 w-full text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
              />
            </label>

            <div className="flex items-center justify-between gap-3">
              <p className="text-[11px] text-slate-500 dark:text-slate-400 flex-1">
                The pupil will <span className="font-bold">not</span> be notified. This is a calm operational record only.
              </p>
              <button
                type="button"
                data-testid="no-show-confirm-btn"
                onClick={submit}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-bold rounded-xl bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 disabled:opacity-50"
              >
                <Check className="w-3.5 h-3.5" />
                {submitting ? "Recording…" : "Confirm no-show"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
